import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation } from "@tanstack/react-query";
import createAxpoCounterInvoiceBatch, {
  AxpoCounterInvoiceBatchFileRef,
  AxpoCounterInvoiceBatchResponse,
  getAxpoCounterInvoiceBatchUploadUrl,
} from "../http/createAxpoCounterInvoiceBatch";
import putSiteFile from "../http/putSiteFile";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export interface CreateAxpoCounterInvoiceBatchPayload {
  files: File[];
  onUploadProgress?: (current: number, total: number) => void;
}

const useCreateAxpoCounterInvoiceBatch = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useMutation(
    async ({
      files,
      onUploadProgress,
    }: CreateAxpoCounterInvoiceBatchPayload): Promise<AxpoCounterInvoiceBatchResponse> => {
      const authToken = await getAuthTokenBlocking(getToken, relogin);

      // Step 1: presigned-URL upload each file directly to S3 (the
      // batch API endpoint would 413 on anything past ~10MB of
      // base64-in-body). Mirrors the per-site
      // `getSiteFileUploadUrl` + `putSiteFile` pattern.
      const refs: AxpoCounterInvoiceBatchFileRef[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (onUploadProgress) onUploadProgress(i + 1, files.length);
        const contentType =
          file.type ||
          (file.name.toLowerCase().endsWith(".pdf")
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
        const { url, s3Key } = await getAxpoCounterInvoiceBatchUploadUrl(
          authToken,
          { fileName: file.name, contentType }
        );
        await putSiteFile(url, contentType, file);
        refs.push({ s3Key, fileName: file.name });
      }

      // Step 2: POST refs to the batch endpoint to pre-validate +
      // kick off the LRO.
      return createAxpoCounterInvoiceBatch(authToken, { files: refs });
    }
  );
};

export default useCreateAxpoCounterInvoiceBatch;
